import { SelectChangeEvent } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getAxios } from "../../utils/endpoints";
import { ReportDetail } from "../../pages/ReportDetail/interfaces";
import dayjs, { Dayjs } from "dayjs";

type IState = {
    timeInterval: string;
    timeZone: string;
    fileFormat: string;
    every: string;
    at: any;
    startDate: Date;
    endDate: Date;
}

interface JSONBody {
    reportId?: number;
    reportType: string;
    folderId: number;
    reportTitle: string;
    description: string;
    shortDescription: string;
    sendEmailNotification: boolean;
    emailsToNotify: string[];
    fileFormat: string;
    selectedColumnsIds: number[];
    selectedFiltersIds: number[];
    filterDetails: any;
    groupBy: number[];
    subGroupBy: number[];
    isScheduled: boolean;
    timeInterval: string;
    timeZone: string;
    frequency: number;
    time: string;
    dayPreference: string;
    startingDate: Date | null;
    endingDate: Date | null;
    id?: number;
}

const INITIAL_STATE: IState = {
    timeInterval: "",
    at: null,
    startDate: new Date(),
    endDate: new Date(),
    every: "",
    timeZone: "",
    fileFormat: ""
}

export const useScheduleDialog = (id: string) => {
    const [state, setState] = useState<IState>(INITIAL_STATE);
    const [emails, setEmails] = useState<string[]>([""]);

    const [snackbarState, setSnackbarState] = useState({
        message: "",
        type: "",
        isOpen: false
    });

    const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);

    const [isScheduled, setIsScheduled] = useState<boolean>(false);

    const onCloseSnackbar = () => setSnackbarState((prevState) => ({ ...prevState, isOpen: false }));
    const onCloseWarningModal = () => setOpenWarningModal(false);

    const addNewEmailAdress = () => {
        if (emails.length >= 4) {
            return;
        }

        setEmails([...emails, ""]);
    }

    const onChangeSelect = (event: SelectChangeEvent<unknown>) => {
        const { name, value } = event.target;

        setState((prevState) => ({
            ...prevState,
            [name]: value as string
        }));
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        const arr = [...emails];

        arr[index] = event.target.value;

        setEmails(arr);
    }

    const onChangeDay = (newDay: any) => {
        setState((prevState) => ({
            ...prevState,
            at: newDay
        }));
    }

    const onChangeSwitch = () => {
        setIsScheduled(!isScheduled);
    }

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => ({
            ...prevState,
            fileFormat: event.target.value
        }));
    }

    const onFetchSchedule = async (): Promise<void> => {
        const response: any = await getAxios(`/reports/custom/${id}`, "GET");

        const data = await response.data as ReportDetail;

        const time = {
            hour: data.time.split(":")[0],
            minute: data.time.split(":")[1]
        }

        
        if (data.emailsToNotify) {
            setEmails(data.emailsToNotify);
        }

        setIsScheduled(data.isScheduled);


        setState((prevState) => ({
            ...prevState,
            timeInterval: data.timeInterval,
            timeZone: data.timeZone,
            every: data.dayPreference,
            fileFormat: data.fileFormat,
            startDate: data.startingDate,
            endDate: data.endingDate,
            at: dayjs('2018-01-01').set("hour", parseInt(time.hour)).set("minute", parseInt(time.minute)), 
        }));
    }

    const onSchedule = async (
        id: string,
        reportType: string,
        folderId: number,
        reportTitle: string,
        description: string,
        columnIds: number[],
        filterIds: number[],
        filterDetails: any,
        groupBy: number[],
        subGroupBy: number[]

    ): Promise<void> => {
        if (isScheduled) {
            for (const key in state) {
                const objKey = key as keyof typeof state;

                if (typeof state[objKey] === "string") {
                    if (state[objKey] === "") {
                        setOpenWarningModal(true);
                        return;
                    }
                }
            }
        }

        try {
            const JsonBody: JSONBody = {
                reportType: reportType,
                folderId: folderId,
                reportTitle: reportTitle,
                description: description,
                shortDescription: "",
                sendEmailNotification: isScheduled,
                emailsToNotify: isScheduled ? emails.filter((val) => (val !== "")) : [],
                fileFormat: state.fileFormat,
                selectedColumnsIds: columnIds,
                selectedFiltersIds: filterIds,
                filterDetails: filterDetails,
                groupBy: groupBy,
                subGroupBy: subGroupBy,
                isScheduled: isScheduled,
                timeInterval: isScheduled ? state.timeInterval : "",
                timeZone: isScheduled ? state.timeZone : "",
                frequency: isScheduled ? parseInt(state.every) : 0,
                time: isScheduled ? `${state.at.$H}:${state.at.$m}` : "",
                dayPreference: isScheduled ? state.every as string : "",
                startingDate: isScheduled ? state.startDate : new Date(),
                endingDate: isScheduled ? state.endDate : new Date()
            }
    
            const response = await getAxios(`/reports/custom/schedule/${id}`, "PUT", {
                JSONString: JSON.stringify(JsonBody)
            });

            setSnackbarState({
                isOpen: true,
                message: "Report has been scheduled",
                type: "success"
            })

        } catch (error) {
            setSnackbarState({
                isOpen: true,
                message: "Something went wrong",
                type: "error"
            })            
        }
    }

    useEffect(() => {
        onFetchSchedule();
    }, []);

    return {
        state,
        snackbarState,
        emails,
        openWarningModal,
        isScheduled,
        onChangeSwitch,
        onCloseSnackbar,
        onCloseWarningModal,
        addNewEmailAdress,
        onChange,
        onChangeSelect,
        onChangeRadio,
        onChangeDay,
        onSchedule
    }
}